import { useState } from 'react';
import { useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount } from '../hooks/useAccounts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createAccountSchema, type Account, type CreateAccountInput } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

export function AccountsPage() {
  const { data: accounts, isLoading } = useAccounts();
  const createMutation = useCreateAccount();
  const updateMutation = useUpdateAccount();
  const deleteMutation = useDeleteAccount();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateAccountInput>({
    resolver: zodResolver(createAccountSchema) as never,
  });

  function openNew() {
    setEditing(null);
    reset({ account_number: '', title: '', is_ira: false, ira_custodian: '' });
    setModalOpen(true);
  }

  function openEdit(row: Account) {
    setEditing(row);
    reset({
      account_number: row.account_number,
      title: row.title,
      is_ira: row.is_ira,
      ira_custodian: row.ira_custodian ?? '',
    });
    setModalOpen(true);
  }

  async function onSubmit(data: CreateAccountInput) {
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...data });
        toast.success('Account updated');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Account created');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save account');
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Deactivate this account?')) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Account deactivated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-silver-500">{accounts?.length ?? 0} accounts</p>
        <Button onClick={openNew}><PlusIcon className="h-4 w-4" /> Add Account</Button>
      </div>

      <Card padding={false}>
        <Table<Account>
          loading={isLoading}
          data={accounts ?? []}
          onRowClick={openEdit}
          columns={[
            { key: 'account_number', header: 'Account #' },
            { key: 'title', header: 'Title' },
            { key: 'is_ira', header: 'IRA', render: (r) => r.is_ira ? 'Yes' : 'No' },
            { key: 'ira_custodian', header: 'IRA Custodian' },
            { key: 'is_active', header: 'Status', render: (r) => <Badge>{r.is_active ? 'active' : 'inactive'}</Badge> },
            {
              key: 'actions', header: '',
              render: (r) => (
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}>
                  Deactivate
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Account' : 'New Account'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Account Number" {...register('account_number')} error={errors.account_number?.message} />
          <Input label="Title" {...register('title')} error={errors.title?.message} />
          <div className="flex items-center gap-2">
            <input type="checkbox" id="is_ira" {...register('is_ira')} className="h-4 w-4 rounded border-silver-300 text-gold-600" />
            <label htmlFor="is_ira" className="text-sm text-silver-700">IRA Account</label>
          </div>
          <Input label="IRA Custodian" {...register('ira_custodian')} error={errors.ira_custodian?.message} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
